import { useOutletContext } from 'react-router-dom';
import { Stack } from '@mui/material';

import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import { getTodayAnalytics } from 'api/rest/analytics';

import useAnalyticsData from '../../hooks/useAnalyticsData';
import useAnalyticsActions from '../../hooks/useAnalyticsActions';
import { AnalyticsError, AnalyticsLoading } from '../../components/AnalyticsState';
import InlineMessageComposer from '../../components/InlineMessageComposer';
import AlertsSection from './AlertsSection';
import RoleHealthSection from './RoleHealthSection';
import RevenueAtRiskSection from './RevenueAtRiskSection';

interface AnalyticsContext {
    isOwner: boolean;
}

const TodayView = () => {
    const { isOwner } = useOutletContext<AnalyticsContext>();
    const { data, loading, error, refetch } = useAnalyticsData(() => getTodayAnalytics(), []);
    const actions = useAnalyticsActions(refetch);

    if (loading) {
        return <AnalyticsLoading />;
    }

    if (error || !data) {
        return (
            <AnalyticsError message={error || 'Failed to load analytics data. Please try again.'} />
        );
    }

    return (
        <Stack spacing={2}>
            <AlertsSection alerts={data.alerts} actions={actions} isOwner={isOwner} />
            <RoleHealthSection actions={actions} isOwner={isOwner} />
            <RevenueAtRiskSection data={data.revenueAtRisk} />

            {actions.confirmDialog.open && (
                <ConfirmationDialog
                    title={actions.confirmDialog.title}
                    content={actions.confirmDialog.content}
                    loading={actions.confirmDialog.loading}
                    onClose={actions.closeConfirmDialog}
                    onConfirm={actions.confirmDialog.onConfirm}
                />
            )}

            <InlineMessageComposer
                open={actions.messageComposer.open}
                recipientName={actions.messageComposer.recipientName}
                template={actions.messageComposer.template}
                onClose={actions.closeMessageComposer}
                onSend={actions.sendMessage}
            />
        </Stack>
    );
};

export default TodayView;
